import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { ROUTES } from "../components/shared/constant";
import { useGetUserPreferencesQuery } from "@/store/api/preferencesApi";
import { useWorkforceGate } from "../views/workforce/hooks/useWorkforceGate";
import { useEmployeeGate } from "./useEmployeeGate";

export interface DefaultLandingRedirectOptions {
  /**
   * True while another navigation owner is in charge — permissions still
   * resolving, a custom domain, or a natural user-portal user. The redirect
   * waits rather than racing them.
   */
  blocked: boolean;
}

/**
 * Sends the user to their preferred product on app entry.
 *
 * Fires AT MOST ONCE per page load. That guard is load-bearing: the header
 * ProductSwitcher returns to Service Desk by navigating to "/", so a redirect
 * that ran on every visit to "/" would make Service Desk unreachable for anyone
 * whose default is Workforce or skyEmployee.
 *
 * Each product redirects only when its own gate passes; otherwise the user
 * stays on Service Desk.
 */
export function useDefaultLandingRedirect({ blocked }: DefaultLandingRedirectOptions): void {
  const navigate = useNavigate();
  const location = useLocation();
  const workforceGate = useWorkforceGate();
  const employeeGate = useEmployeeGate();
  const { data, isLoading } = useGetUserPreferencesQuery();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (hasRedirected.current) return;
    if (blocked || isLoading || workforceGate.isLoading || employeeGate.isLoading) return;
    // Any explicit destination beats the preference.
    if (location.pathname !== ROUTES.HOME) return;

    const preference = data?.uiPreferences?.defaultLanding;
    const target =
      preference === "workforce" && workforceGate.enabled
        ? ROUTES.WORKFORCE
        : preference === "employee" && employeeGate.enabled
          ? ROUTES.EMPLOYEE
          : null;
    if (!target) return;

    hasRedirected.current = true;
    // window.location.search carries instanceid, same as the sibling redirect
    // in AppLayout.
    navigate(target + window.location.search, { replace: true });
  }, [
    blocked,
    isLoading,
    workforceGate.isLoading,
    workforceGate.enabled,
    employeeGate.isLoading,
    employeeGate.enabled,
    location.pathname,
    data,
    navigate,
  ]);
}
